import { createElement, type ReactNode } from 'react'
import { type ProColumns } from '@ant-design/pro-components'
import { auditedListColumns, type useMemberCreatorOptions } from '@/features/access/index.ts'
import { proRemoteSearch } from '@/utils/pro-select-search.ts'
import { OrderCustomerCell, OrderNoteCell, OrderNumberGroupCell, OrderStatusTag } from './order-list-cells.tsx'
import {
  ORDER_LIST_COLUMN_WIDTHS,
  type OrderListAuditRow,
  type OrderListBaseRow,
  type OrderListFilterOptions,
  type OrderStatusMap
} from './order-list-types.ts'

export { ORDER_LIST_COLUMN_WIDTHS } from './order-list-types.ts'
export type {
  OrderListAuditRow,
  OrderListBaseRow,
  OrderListFilterOption,
  OrderListFilterOptions,
  OrderListSearchableFilter,
  OrderStatusMap
} from './order-list-types.ts'

function multiSelectFieldProps(filter: OrderListFilterOptions['business']) {
  return {
    allowClear: true,
    mode: 'multiple' as const,
    options: filter.options,
    loading: filter.loading,
    ...proRemoteSearch(filter.onSearch)
  }
}

export function createOrderListBaseColumns<TRow extends OrderListBaseRow>({
  keywordPlaceholder,
  statusMap,
  filterOptions,
  renderSubject,
  renderOrderNumber,
  renderOrderGroup,
  renderCustomer
}: {
  keywordPlaceholder: string
  statusMap: OrderStatusMap<TRow['status']>
  filterOptions: OrderListFilterOptions
  renderSubject: (order: TRow) => ReactNode
  renderOrderNumber?: (order: TRow) => ReactNode
  renderOrderGroup?: (order: TRow) => ReactNode
  renderCustomer?: (order: TRow) => ReactNode
}): ProColumns<TRow>[] {
  return [
    // {
    //   title: 'ID',
    //   dataIndex: 'idSearch',
    //   hideInTable: true,
    //   hidden: true,
    //   fieldProps: { allowClear: true, inputMode: 'numeric', placeholder: '输入订单 ID' }
    // },
    {
      title: '关键词 / ID',
      dataIndex: 'keyword',
      hideInTable: true,
      fieldProps: { allowClear: true, placeholder: keywordPlaceholder }
    },
    {
      title: '业务状态',
      dataIndex: 'statusFilter',
      hideInTable: true,
      valueType: 'select',
      valueEnum: Object.fromEntries(
        Object.entries<OrderStatusMap<string>[string]>(statusMap).filter(([status]) => status !== 'archived')
      ),
      fieldProps: { allowClear: true, mode: 'multiple' }
    },
    {
      title: '归档状态',
      dataIndex: 'archiveStatus',
      hideInTable: true,
      valueType: 'select',
      valueEnum: {
        unarchived: { text: '未归档' },
        archived: { text: '已归档' },
        all: { text: '全部' }
      },
      fieldProps: { allowClear: false }
    },
    {
      title: '业务类型',
      dataIndex: 'businessId',
      hideInTable: true,
      valueType: 'select',
      fieldProps: multiSelectFieldProps(filterOptions.business)
    },
    {
      title: '客户',
      dataIndex: 'customerId',
      hideInTable: true,
      valueType: 'select',
      fieldProps: multiSelectFieldProps(filterOptions.customer)
    },
    {
      title: '业务员',
      dataIndex: 'salespersonId',
      hideInTable: true,
      valueType: 'select',
      fieldProps: multiSelectFieldProps(filterOptions.salesperson)
    },
    {
      title: '服务对象',
      dataIndex: 'subjectId',
      hideInTable: true,
      valueType: 'select',
      fieldProps: multiSelectFieldProps(filterOptions.subject)
    },
    ...(filterOptions.site
      ? [
          {
            title: '地址',
            dataIndex: 'siteId',
            hideInTable: true,
            valueType: 'select' as const,
            fieldProps: multiSelectFieldProps(filterOptions.site)
          }
        ]
      : []),
    { title: 'ID', dataIndex: 'id', copyable: true, search: false, width: ORDER_LIST_COLUMN_WIDTHS.id },
    {
      title: '订单号 / 订单组',
      dataIndex: 'orderNo',
      search: false,
      width: ORDER_LIST_COLUMN_WIDTHS.orderNoGroup,
      render: (_, order) =>
        createElement(OrderNumberGroupCell, {
          orderNo: order.orderNo,
          orderGroupName: order.orderGroupName,
          orderNumber: renderOrderNumber?.(order),
          orderGroup: renderOrderGroup?.(order)
        })
    },
    {
      title: '状态',
      dataIndex: 'status',
      search: false,
      valueEnum: statusMap,
      width: ORDER_LIST_COLUMN_WIDTHS.status,
      render: (_, order) =>
        createElement(OrderStatusTag, { status: order.status, statusMap, archivedAt: order.archivedAt })
    },
    {
      title: '客户',
      dataIndex: 'customerName',
      search: false,
      width: ORDER_LIST_COLUMN_WIDTHS.customer,
      render: (_, order) =>
        createElement(OrderCustomerCell, { customerName: order.customerName, children: renderCustomer?.(order) })
    },
    {
      title: '服务对象',
      dataIndex: 'subjectName',
      search: false,
      width: ORDER_LIST_COLUMN_WIDTHS.subject,
      render: (_, order) => renderSubject(order)
    },
    {
      title: '业务员',
      dataIndex: 'salespersonName',
      search: false,
      width: ORDER_LIST_COLUMN_WIDTHS.salesperson,
      renderText: (value?: string | null) => value || '—'
    }
  ]
}

type OrderListAuditSearch = {
  creatorMemberId?: number
  createdFrom?: string
  createdTo?: string
  sortField?: string
  sortOrder?: 'asc' | 'desc'
}

export function createOrderListAuditColumns<TRow extends OrderListAuditRow>({
  search,
  memberCreator
}: {
  search: OrderListAuditSearch
  memberCreator: ReturnType<typeof useMemberCreatorOptions>
}): ProColumns<TRow>[] {
  return [
    {
      title: '备注',
      dataIndex: 'note',
      fieldProps: { allowClear: true, maxLength: 255, placeholder: '输入备注关键词' },
      width: ORDER_LIST_COLUMN_WIDTHS.note,
      render: (_, order) => createElement(OrderNoteCell, { note: order.note })
    },
    ...auditedListColumns<TRow>({
      search,
      memberCreator,
      creatorWidth: ORDER_LIST_COLUMN_WIDTHS.createdBy,
      createdAtWidth: ORDER_LIST_COLUMN_WIDTHS.createdAt
    }),
    {
      title: '更新时间',
      dataIndex: 'updatedAt',
      search: false,
      valueType: 'dateTime',
      width: ORDER_LIST_COLUMN_WIDTHS.updatedAt
    }
  ]
}
